import React, { useEffect, useMemo, useState } from "react";
import { GridFour, ChartBar, Info, ArrowsHorizontal, TrendUp, TrendDown, PlayCircle, Plus, X, MagnifyingGlass, DownloadSimple, Trash, Fire } from "@phosphor-icons/react";
import { toast } from "sonner";
import { timingPoolMatrix, timingCurrentFpl, timingPoolMatch } from "../../lib/timing-pool-api";
import { exportMatrixXlsx } from "../../lib/matrix-export";
import { SavedLists } from "./SavedLists";
import { useListKeyboardNav } from "../../lib/use-list-keyboard-nav";

const MATRIX_STORAGE_KEY = "fpl-matrix-players-v1";
const stripDia = (s) => (s || "").toString().normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "").toLowerCase();

// ---------------------------------------------------------------------------
// Fixture Matrix — 30-player × 38-GW (or 6-phase) heat map of expected PPG.
// Each cell blends historical PPG vs opponent + phase + H/A.
// Row expansion shows: long-term PPG, best/worst opponents, phase totals.
// ---------------------------------------------------------------------------

// Color scale by absolute expected PPG (green→red gradient)
function cellColor(ppg, blank) {
    if (blank) return "bg-black/40 text-white/25";
    if (ppg >= 8) return "bg-fa-mint text-black font-black";
    if (ppg >= 6.5) return "bg-fa-mint/60 text-black font-bold";
    if (ppg >= 5) return "bg-fa-mint/30 text-fa-mint font-bold";
    if (ppg >= 4) return "bg-white/[0.06] text-white font-medium";
    if (ppg >= 3) return "bg-orange-500/25 text-orange-100";
    return "bg-red-500/30 text-red-200 font-bold";
}

// Color scale by deviation from player's own average (opportunity signal)
function deviationColor(dev, blank) {
    if (blank) return "bg-black/40 text-white/25";
    if (dev >= 1.5) return "bg-fa-mint text-black font-black";
    if (dev >= 0.75) return "bg-fa-mint/60 text-black font-bold";
    if (dev >= 0.25) return "bg-fa-mint/30 text-fa-mint font-bold";
    if (dev >= -0.25) return "bg-white/[0.05] text-white/70";
    if (dev >= -0.75) return "bg-orange-500/25 text-orange-100";
    if (dev >= -1.5) return "bg-red-500/30 text-red-200 font-bold";
    return "bg-red-600/50 text-red-100 font-black";
}

function MatrixCell({ c, active, onClick, metric }) {
    const isDev = metric === "deviation";
    const bg = c.blank
        ? "bg-black/40 text-white/25"
        : isDev ? deviationColor(c.deviation, c.blank) : cellColor(c.expected_ppg, c.blank);
    const primary = c.blank
        ? "—"
        : isDev
            ? (c.deviation > 0 ? `+${c.deviation.toFixed(1)}` : c.deviation.toFixed(1))
            : c.expected_ppg.toFixed(1);
    const opp = c.opp_short || (c.opponent ? c.opponent.slice(0, 3).toUpperCase() : "—");
    const tooltip = c.blank
        ? "Blank GW"
        : `GW${c.gw} · ${c.opponent}${c.is_home ? " (H)" : " (A)"}\nHist ${c.hist_games}g${c.hist_ppg != null ? ` @ ${c.hist_ppg}` : ""}\nExpected ${c.expected_ppg} · odchylka ${c.deviation > 0 ? "+" : ""}${c.deviation}${c.haul_hot && c.haul_stat ? `\n🔥 Hauluje proti: ${c.haul_stat.hauls}/${c.haul_stat.games} zápasů (${c.haul_stat.rate} %)` : ""}`;
    return (
        <td
            title={tooltip}
            onClick={onClick}
            data-testid={`matrix-cell-${c.gw}`}
            className={`relative ${bg} text-center tabular-nums font-mono px-1 py-0.5 border-r border-b border-black/40 cursor-pointer ${active ? "outline outline-2 outline-fa-yellow" : ""}`}
        >
            {c.haul_hot && (
                <Fire size={9} weight="fill" className="absolute top-0 right-0 text-orange-400" data-testid={`matrix-haul-hot-${c.gw}`} />
            )}
            <div className="text-[8px] leading-tight opacity-75 uppercase tracking-wider">
                {opp}{c.is_home != null && <span className="opacity-60">{c.is_home ? "·H" : "·A"}</span>}
            </div>
            <div className="text-[11px] leading-tight font-bold">{primary}</div>
        </td>
    );
}

function PhaseCell({ ph, metric }) {
    const isDev = metric === "deviation";
    const val = isDev ? (ph?.deviation ?? 0) : (ph?.avg ?? 0);
    const bg = ph?.games === 0
        ? "bg-black/40 text-white/25"
        : isDev ? deviationColor(val, false) : cellColor(val, false);
    const primary = isDev
        ? (val > 0 ? `+${val.toFixed(1)}` : val.toFixed(1))
        : val.toFixed(1);
    return (
        <td
            title={`Průměr ${ph?.avg} · odchylka ${ph?.deviation > 0 ? "+" : ""}${ph?.deviation} · celkem ${ph?.total?.toFixed?.(1)} pts · ${ph?.games || 0} GW`}
            className={`${bg} text-center tabular-nums font-mono text-[11px] px-2 py-1.5 border-r border-b border-black/40`}
        >
            <div className="font-bold">{primary}</div>
            <div className="text-[8px] opacity-70">{ph?.total?.toFixed(0) || 0}p</div>
        </td>
    );
}

// ---------------------------------------------------------------------------
// Toolbar above the matrix: search-and-add players from FPL DB, import pool,
// clear-all. Rendered both in empty state and above the populated matrix.
// ---------------------------------------------------------------------------
function MatrixControls({
    query, setQuery, searchKeyDown, filteredFpl, selectedIndex, itemRef,
    addFromFpl, addingId, rosterLoading,
    matrixCount, poolCount, importFromPool, clearAll,
    matrixPlayers, onLoadList,
    marginalCount, removeMarginals,
}) {
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3 flex flex-col gap-2" data-testid="matrix-controls">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/45">
                    Matice: <strong className="text-white">{matrixCount}</strong> hráčů
                </span>
                <button
                    type="button"
                    onClick={importFromPool}
                    disabled={poolCount === 0}
                    data-testid="matrix-import-pool"
                    className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-fa-sky/40 text-fa-sky hover:bg-fa-sky hover:text-black disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-fa-sky flex items-center gap-1"
                    title={poolCount === 0 ? "Pool je prázdný" : `Přidat ${poolCount} hráčů z poolu`}
                >
                    <DownloadSimple size={10} weight="bold" />
                    Import z poolu ({poolCount})
                </button>
                <button
                    type="button"
                    onClick={clearAll}
                    disabled={matrixCount === 0}
                    data-testid="matrix-clear-all"
                    className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-red-400/40 text-red-300 hover:bg-red-500 hover:text-black disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-red-300 flex items-center gap-1"
                >
                    <Trash size={10} weight="bold" />
                    Vyprázdnit
                </button>
                {marginalCount > 0 && (
                    <button
                        type="button"
                        onClick={removeMarginals}
                        data-testid="matrix-remove-marginals"
                        className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-orange-400/40 text-orange-300 hover:bg-orange-400 hover:text-black flex items-center gap-1"
                        title="Odebrat hráče bez jediné minuty a bodu v aktuální sezóně (mladíci, rezervy)"
                    >
                        <X size={10} weight="bold" />
                        Odebrat marginály ({marginalCount})
                    </button>
                )}
                <span className="ml-auto">
                    <SavedLists players={matrixPlayers} source="matrix" onLoad={onLoadList} />
                </span>
            </div>
            <div className="relative">
                <MagnifyingGlass size={11} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={searchKeyDown}
                    placeholder={rosterLoading ? "Načítám hráče…" : "Vyhledej hráče v FPL databázi a přidej do matice…"}
                    disabled={rosterLoading}
                    data-testid="matrix-search"
                    className="w-full bg-black/40 border border-white/15 pl-7 pr-16 py-1.5 font-mono text-[11px] focus:outline-none focus:border-fa-sky disabled:opacity-40"
                />
                {query && (
                    <>
                        <kbd className="absolute right-7 top-1/2 -translate-y-1/2 font-mono text-[8px] uppercase tracking-widest text-white/35 border border-white/15 px-1 py-[1px] pointer-events-none" aria-hidden>ESC</kbd>
                        <button type="button" onClick={() => setQuery("")}
                                aria-label="Zavřít vyhledávání"
                                data-testid="matrix-search-clear"
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/45 hover:text-white p-0.5">
                            <X size={12} weight="bold" />
                        </button>
                    </>
                )}
                {query && filteredFpl.length > 0 && (
                    <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto border border-white/15 bg-[#0d0d0d] shadow-xl">
                        {filteredFpl.map((p, i) => (
                            <button
                                key={p.fpl_id}
                                type="button"
                                ref={itemRef(i)}
                                onClick={() => addFromFpl(p)}
                                disabled={addingId === p.fpl_id}
                                data-testid={`matrix-search-add-${p.fpl_id}`}
                                className={`w-full text-left px-2 py-1.5 border-b border-white/5 last:border-0 flex items-center justify-between gap-2 disabled:opacity-40 ${i === selectedIndex ? "bg-fa-sky/20" : "hover:bg-fa-sky/10"}`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="font-mono text-[9px] uppercase tracking-widest text-white/45 w-8">{p.position}</span>
                                    <span className="font-display font-black text-[13px] truncate">{p.full_name}</span>
                                    <span className="font-mono text-[9px] text-white/50">{p.team}</span>
                                    {p.is_promoted && (
                                        <span
                                            className="font-mono text-[8px] uppercase tracking-widest px-1 py-[1px] bg-fa-yellow/25 text-fa-yellow border border-fa-yellow/50 whitespace-nowrap"
                                            title="Nově postupující tým — FPL data zatím nejsou"
                                        >
                                            Nově
                                        </span>
                                    )}
                                </div>
                                <span className="font-mono text-[10px] tabular-nums text-fa-yellow font-bold whitespace-nowrap">
                                    {p.price == null ? "£—" : `£${p.price.toFixed(1)}m`}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function OpponentPill({ o, accent }) {
    return (
        <span className={`font-mono text-[10px] tabular-nums px-1.5 py-0.5 border ${accent}`}>
            {o.opponent} <span className="opacity-60">{o.ppg}·{o.games}g</span>
        </span>
    );
}

function ExpandedRow({ p, mode, colCount }) {
    const cells = p.cells || [];
    const bestFx = [...cells.filter((c) => !c.blank)]
        .sort((a, b) => b.expected_ppg - a.expected_ppg).slice(0, 3);
    const worstFx = [...cells.filter((c) => !c.blank)]
        .sort((a, b) => a.expected_ppg - b.expected_ppg).slice(0, 3);
    return (
        <tr className="bg-black/40">
            <td colSpan={colCount} className="px-3 py-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-white">
                    <div className="border border-white/10 bg-[#0d0d0d] p-2">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Historie</div>
                        <div className="font-display font-black text-2xl tabular-nums text-fa-yellow">
                            {p.overall_ppg.toFixed(1)}
                        </div>
                        <div className="font-mono text-[9px] text-white/50">
                            Dlouhodobý průměr PPG · {p.games_played_history} zápasů
                        </div>
                    </div>
                    <div className="border border-white/10 bg-[#0d0d0d] p-2">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Očekávané celkem</div>
                        <div className="font-display font-black text-2xl tabular-nums text-fa-mint">
                            {p.total_expected.toFixed(0)}
                        </div>
                        <div className="font-mono text-[9px] text-white/50">
                            Součet 38 GW · avg {(p.total_expected / 38).toFixed(1)} PPG
                        </div>
                    </div>
                    <div className="border border-fa-mint/30 bg-fa-mint/[0.03] p-2 md:col-span-2">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint mb-1 flex items-center gap-1">
                            <TrendUp size={10} weight="fill" /> Nejlepší matchupy
                        </div>
                        <div className="flex flex-wrap gap-1 mb-2">
                            {p.best_opponents.map((o) => (
                                <OpponentPill key={o.opponent} o={o} accent="text-fa-mint border-fa-mint/50 bg-fa-mint/[0.06]" />
                            ))}
                            {p.best_opponents.length === 0 && (
                                <span className="font-mono text-[10px] text-white/40 italic">Málo dat</span>
                            )}
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-2 mb-1">Top 3 fixtures 2025-26</div>
                        <div className="flex flex-wrap gap-1">
                            {bestFx.map((c) => (
                                <span key={c.gw} className="font-mono text-[10px] tabular-nums px-1.5 py-0.5 border border-fa-mint/40 text-fa-mint">
                                    GW{c.gw} vs {c.opponent}{c.is_home ? " H" : " A"} · {c.expected_ppg.toFixed(1)}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="border border-red-400/30 bg-red-500/[0.03] p-2 md:col-span-4">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-red-200 mb-1 flex items-center gap-1">
                            <TrendDown size={10} weight="fill" /> Nejtěžší matchupy
                        </div>
                        <div className="flex flex-wrap gap-1 mb-2">
                            {p.worst_opponents.map((o) => (
                                <OpponentPill key={o.opponent} o={o} accent="text-red-200 border-red-400/50 bg-red-500/[0.06]" />
                            ))}
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-1 mb-1">Bottom 3 fixtures 2025-26</div>
                        <div className="flex flex-wrap gap-1">
                            {worstFx.map((c) => (
                                <span key={c.gw} className="font-mono text-[10px] tabular-nums px-1.5 py-0.5 border border-red-400/40 text-red-200">
                                    GW{c.gw} vs {c.opponent}{c.is_home ? " H" : " A"} · {c.expected_ppg.toFixed(1)}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </td>
        </tr>
    );
}

export default function FixtureMatrix({ lang, sharedPool }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("gw"); // "gw" | "phase"
    const [metric, setMetric] = useState("ppg"); // "ppg" | "deviation"
    const [expandedId, setExpandedId] = useState(null);
    const [sortBy, setSortBy] = useState("total_expected");
    const [sortDir, setSortDir] = useState("desc");
    const [posFilter, setPosFilter] = useState("");
    const [teamFilter, setTeamFilter] = useState("");

    // Independent matrix roster — persisted to localStorage so users don't lose
    // their custom picks. Starts empty until user imports from pool or adds manually.
    const [matrixPlayers, setMatrixPlayers] = useState(() => {
        try { return JSON.parse(localStorage.getItem(MATRIX_STORAGE_KEY)) || []; }
        catch (_) { return []; }
    });
    useEffect(() => {
        try { localStorage.setItem(MATRIX_STORAGE_KEY, JSON.stringify(matrixPlayers)); }
        catch (_) { /* localStorage unavailable */ }
    }, [matrixPlayers]);

    // Search over the current FPL player pool (loaded once).
    const [fplRoster, setFplRoster] = useState(null);
    const [rosterLoading, setRosterLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [addingId, setAddingId] = useState(null);

    useEffect(() => {
        setRosterLoading(true);
        timingCurrentFpl()
            .then((d) => setFplRoster(d.players || []))
            .catch(() => setFplRoster([]))
            .finally(() => setRosterLoading(false));
    }, []);

    const { pool } = sharedPool;

    const runMatrix = async (playersOverride) => {
        const src = playersOverride ?? matrixPlayers;
        if (!src.length) { setData(null); return; }
        setLoading(true);
        try {
            const payload = src.map((p) => ({
                fpl_id: p.fpl_id,
                historical_slug: p.historical_slug,
                display_name: p.display_name,
                position: p.position,
                team: p.team,
                current_price: p.current_price == null ? 0 : p.current_price,
                ownership_pct: p.ownership_pct,
                availability_pct: 100,
            }));
            const r = await timingPoolMatrix(payload);
            setData(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Načtení matice selhalo");
        } finally {
            setLoading(false);
        }
    };

    // Auto-run whenever matrixPlayers changes.
    useEffect(() => {
        runMatrix();
    }, [matrixPlayers]);

    // Filtered current-FPL search results.
    const filteredFpl = useMemo(() => {
        if (!fplRoster || !query) return [];
        const q = stripDia(query.trim());
        if (!q) return [];
        const inMatrix = new Set(matrixPlayers.map((p) => p.fpl_id));
        return fplRoster
            .filter((p) =>
                stripDia(p.web_name).includes(q)
                || stripDia(p.full_name).includes(q)
                || stripDia(p.team).includes(q))
            .filter((p) => !inMatrix.has(p.fpl_id))
            .sort((a, b) => (b.minutes ?? 0) - (a.minutes ?? 0))
            .slice(0, 20);
    }, [fplRoster, query, matrixPlayers]);

    // A player is "marginal" when FPL data exists but shows zero involvement
    // (registered youth squad members etc.). Promoted-team players are kept.
    const isMarginal = (p) =>
        !p.is_promoted
        && p.source !== "sportmonks"
        && (p.minutes ?? 0) === 0
        && (p.total_points ?? 0) === 0;

    const addFromFpl = async (fp) => {
        setAddingId(fp.fpl_id);
        try {
            // Resolve historical_slug via /pool/match so the matrix has real
            // multi-season data for the newly-added player.
            const r = await timingPoolMatch([{ fpl_id: fp.fpl_id }]);
            const m = r.matches?.[0];
            const c = m?.current || fp;
            setMatrixPlayers((cur) => {
                if (cur.some((x) => x.fpl_id === fp.fpl_id)) return cur;
                return [...cur, {
                    fpl_id: fp.fpl_id,
                    display_name: c.display_name || fp.full_name,
                    historical_slug: m?.historical_slug,
                    position: c.position,
                    team: c.team,
                    current_price: c.price ?? fp.price ?? null,
                    ownership_pct: c.ownership_pct,
                    is_promoted: !!(c.is_promoted || fp.is_promoted),
                }];
            });
            setQuery("");
        } catch (e) {
            toast.error("Přidání selhalo");
        } finally {
            setAddingId(null);
        }
    };

    const removePlayer = (fpl_id) => {
        setMatrixPlayers((cur) => cur.filter((p) => p.fpl_id !== fpl_id));
        if (expandedId === fpl_id) setExpandedId(null);
    };
    const clearAll = () => {
        if (matrixPlayers.length === 0) return;
        if (!window.confirm(`Vyprázdnit celou matici (${matrixPlayers.length} hráčů)?`)) return;
        setMatrixPlayers([]);
        setData(null);
        setExpandedId(null);
    };
    const importFromPool = () => {
        if (!pool.length) {
            toast.info("Pool je prázdný — nejdřív ho sestav v Team Season DNA.");
            return;
        }
        // Merge (dedupe by fpl_id): keep matrix additions, add new pool players.
        setMatrixPlayers((cur) => {
            const existing = new Set(cur.map((p) => p.fpl_id));
            const additions = pool.filter((p) => !existing.has(p.fpl_id));
            if (additions.length === 0) {
                toast.info("Všichni hráči z poolu už v matici jsou.");
                return cur;
            }
            toast.success(`Přidáno ${additions.length} hráčů z poolu.`);
            return [...cur, ...additions.map((p) => ({
                fpl_id: p.fpl_id,
                display_name: p.display_name,
                historical_slug: p.historical_slug,
                position: p.position,
                team: p.team,
                current_price: p.current_price,
                ownership_pct: p.ownership_pct,
            }))];
        });
    };

    // Search keyboard nav — arrow ↓/↑ + Enter to add
    const { selectedIndex, onKeyDown: searchKeyDown, itemRef, resetIndex } = useListKeyboardNav({
        itemsLength: filteredFpl.length,
        onEnter: (i) => filteredFpl[i] && addFromFpl(filteredFpl[i]),
        onEscape: () => setQuery(""),
    });
    useEffect(() => { resetIndex(); }, [query, resetIndex]);

    const sortedPlayers = useMemo(() => {
        if (!data?.players) return [];
        const list = data.players
            .filter((p) => !posFilter || p.position === posFilter)
            .filter((p) => !teamFilter || p.team === teamFilter);
        const getVal = (p) => {
            if (sortBy === "display_name") return p.display_name.toLowerCase();
            if (sortBy === "position") {
                const order = { GKP: 0, DEF: 1, MID: 2, FWD: 3 };
                return order[p.position] ?? 9;
            }
            if (sortBy.startsWith("phase:")) {
                const k = sortBy.slice(6);
                const ph = p.phase_expected?.[k];
                if (!ph) return -Infinity;
                return metric === "deviation" ? (ph.deviation ?? 0) : (ph.avg ?? 0);
            }
            if (sortBy.startsWith("gw:")) {
                const gw = parseInt(sortBy.slice(3), 10);
                const c = p.cells?.find((x) => x.gw === gw);
                if (!c || c.blank) return -Infinity;
                return metric === "deviation" ? c.deviation : c.expected_ppg;
            }
            return p[sortBy] ?? -Infinity;
        };
        return [...list].sort((a, b) => {
            const va = getVal(a), vb = getVal(b);
            if (va === vb) return 0;
            const cmp = va < vb ? -1 : 1;
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [data, sortBy, sortDir, posFilter, teamFilter, metric]);

    // Unique team names available across (a) current matrix data and (b) full
    // FPL roster — so users can also filter to a team they haven't imported yet
    // (and then click "Add whole team").
    const allTeams = useMemo(() => {
        const set = new Set();
        (data?.players || []).forEach((p) => p.team && set.add(p.team));
        (fplRoster || []).forEach((p) => p.team && set.add(p.team));
        return Array.from(set).sort();
    }, [data, fplRoster]);

    // "Přidat celý tým" — dumps all FPL/Sportmonks players from the picked
    // team into the matrix (skipping already-added). Uses /pool/match to
    // resolve historical slugs in bulk.
    const [addingTeam, setAddingTeam] = useState(false);
    const addWholeTeam = async (teamName) => {
        if (!teamName || !fplRoster) return;
        const inMatrix = new Set(matrixPlayers.map((p) => p.fpl_id));
        const candidates = fplRoster
            .filter((p) => p.team === teamName)
            .filter((p) => !inMatrix.has(p.fpl_id))
            .filter((p) => !isMarginal(p) && p.status !== "u")
            .filter((p) => !posFilter || p.position === posFilter);
        if (!candidates.length) {
            toast.info(`Všichni relevantní hráči týmu ${teamName} už v matici jsou.`);
            return;
        }
        setAddingTeam(true);
        try {
            const r = await timingPoolMatch(candidates.map((p) => ({ fpl_id: p.fpl_id })));
            const additions = [];
            for (let i = 0; i < candidates.length; i += 1) {
                const fp = candidates[i];
                const m = r.matches?.[i];
                const c = m?.current || fp;
                additions.push({
                    fpl_id: fp.fpl_id,
                    display_name: c.display_name || fp.full_name,
                    historical_slug: m?.historical_slug,
                    position: c.position,
                    team: c.team,
                    current_price: c.price ?? fp.price ?? null,
                    ownership_pct: c.ownership_pct,
                    is_promoted: !!(c.is_promoted || fp.is_promoted),
                });
            }
            setMatrixPlayers((cur) => [...cur, ...additions]);
            toast.success(`Přidáno ${additions.length} hráčů z týmu ${teamName}.`);
        } catch (e) {
            toast.error("Přidání týmu selhalo");
        } finally {
            setAddingTeam(false);
        }
    };

    // Matrix players with zero involvement in current FPL data (youth etc.).
    const marginalIds = useMemo(() => {
        if (!fplRoster?.length || !matrixPlayers.length) return [];
        const byId = new Map(fplRoster.map((p) => [p.fpl_id, p]));
        return matrixPlayers
            .filter((mp) => {
                const rp = byId.get(mp.fpl_id);
                return rp && isMarginal(rp);
            })
            .map((mp) => mp.fpl_id);
    }, [fplRoster, matrixPlayers]);
    const removeMarginals = () => {
        if (!marginalIds.length) return;
        const ids = new Set(marginalIds);
        setMatrixPlayers((cur) => cur.filter((p) => !ids.has(p.fpl_id)));
        if (ids.has(expandedId)) setExpandedId(null);
        toast.success(`Odebráno ${marginalIds.length} marginálních hráčů.`);
    };

    // Toggle column-header sort: clicking cycles asc → desc → default
    const toggleColSort = (key) => {
        if (sortBy === key) {
            setSortDir((d) => d === "desc" ? "asc" : "desc");
        } else {
            setSortBy(key);
            setSortDir("desc");
        }
    };
    const clearColSort = () => { setSortBy("total_expected"); setSortDir("desc"); };
    const sortIndicator = (key) => sortBy === key ? (sortDir === "desc" ? "↓" : "↑") : "↕";

    if (matrixPlayers.length === 0) {
        return (
            <div className="space-y-4" data-testid="timing-matrix">
                <MatrixControls
                    query={query}
                    setQuery={setQuery}
                    searchKeyDown={searchKeyDown}
                    filteredFpl={filteredFpl}
                    selectedIndex={selectedIndex}
                    itemRef={itemRef}
                    addFromFpl={addFromFpl}
                    addingId={addingId}
                    rosterLoading={rosterLoading}
                    matrixCount={matrixPlayers.length}
                    poolCount={pool.length}
                    importFromPool={importFromPool}
                    clearAll={clearAll}
                    matrixPlayers={matrixPlayers}
                    onLoadList={(ps) => setMatrixPlayers(ps)}
                />
                <div className="border-2 border-dashed border-white/15 bg-[#0d0d0d] p-8 text-center" data-testid="matrix-empty">
                    <GridFour size={32} weight="regular" className="mx-auto text-white/25 mb-2" />
                    <p className="font-mono text-[11px] text-white/50">
                        Matice je prázdná — přidej hráče přes vyhledávání nahoře nebo naimportuj celý pool z Team Season DNA.
                    </p>
                </div>
            </div>
        );
    }

    const colCount = (mode === "gw" ? 38 : 6) + 1;  // +1 for player name column
    const gws = data?.gws || [];
    const phases = data?.phases || [];

    return (
        <div className="space-y-3" data-testid="timing-matrix">
            <MatrixControls
                query={query}
                setQuery={setQuery}
                searchKeyDown={searchKeyDown}
                filteredFpl={filteredFpl}
                selectedIndex={selectedIndex}
                itemRef={itemRef}
                addFromFpl={addFromFpl}
                addingId={addingId}
                rosterLoading={rosterLoading}
                matrixCount={matrixPlayers.length}
                poolCount={pool.length}
                importFromPool={importFromPool}
                clearAll={clearAll}
                matrixPlayers={matrixPlayers}
                onLoadList={(ps) => setMatrixPlayers(ps)}
                marginalCount={marginalIds.length}
                removeMarginals={removeMarginals}
            />
            <header className="border border-white/10 bg-[#0d0d0d] p-4">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <GridFour size={14} weight="fill" className="text-fa-yellow" />
                    <h2 className="font-display font-black text-xl uppercase tracking-tight">
                        Fixture Matice · očekávané body
                    </h2>
                    <div className="ml-auto flex items-center gap-3 flex-wrap" data-testid="matrix-mode-toggle">
                        <button
                            type="button"
                            onClick={() => {
                                if (!sortedPlayers.length) { toast.info("Matice je prázdná — není co exportovat."); return; }
                                try {
                                    exportMatrixXlsx({ players: sortedPlayers, gws, phases });
                                    toast.success(`Export hotový — ${sortedPlayers.length} hráčů, 6 listů vč. vysvětlivek.`);
                                } catch (e) {
                                    toast.error("Export selhal");
                                }
                            }}
                            data-testid="matrix-export-xls"
                            className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-fa-mint/40 text-fa-mint hover:bg-fa-mint hover:text-black flex items-center gap-1"
                            title="Stáhne .xlsx: Souhrn, GW body, GW odchylka, GW soupeři, Fáze + Vysvětlivky. Respektuje aktuální filtry a řazení."
                        >
                            <DownloadSimple size={11} weight="bold" />
                            Export XLS
                        </button>
                        <div className="flex items-center gap-1">
                            <span className="font-mono text-[9px] uppercase tracking-widest text-white/40 mr-1">Metrika:</span>
                            <button
                                type="button"
                                onClick={() => setMetric("ppg")}
                                data-testid="matrix-metric-ppg"
                                className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${metric === "ppg" ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                            >
                                Body
                            </button>
                            <button
                                type="button"
                                onClick={() => setMetric("deviation")}
                                data-testid="matrix-metric-deviation"
                                className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${metric === "deviation" ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                            >
                                Odchylka
                            </button>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="font-mono text-[9px] uppercase tracking-widest text-white/40 mr-1">Rozsah:</span>
                            <button
                                type="button"
                                onClick={() => setMode("gw")}
                                data-testid="matrix-mode-gw"
                                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${mode === "gw" ? "bg-fa-yellow text-black border-fa-yellow font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                            >
                                38 GW
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode("phase")}
                                data-testid="matrix-mode-phase"
                                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${mode === "phase" ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                            >
                                Fáze
                            </button>
                        </div>
                    </div>
                </div>
                <p className="font-mono text-[11px] text-white/60 leading-relaxed">
                    Každá buňka = <strong>{metric === "ppg" ? "očekávané body" : "odchylka od dlouhodobého průměru hráče"}</strong> v daném GW (blend fáze × soupeř × H/A).
                    {metric === "deviation" && " Zelená = nadprůměrná příležitost, červená = pod-průměrný matchup."}
                    {" "}Klikni na řádek pro detail.
                </p>
            </header>

            {/* Filter + sort controls */}
            <div className="flex items-center gap-2 flex-wrap font-mono text-[10px] uppercase tracking-widest">
                <span className="text-white/45">Filtr:</span>
                {["", "GKP", "DEF", "MID", "FWD"].map((p) => (
                    <button
                        key={p || "all"}
                        type="button"
                        onClick={() => setPosFilter(p)}
                        data-testid={`matrix-filter-${p || "all"}`}
                        className={`px-2 py-1 border ${posFilter === p ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                    >
                        {p || "Vše"}
                    </button>
                ))}
                <span className="text-white/45 ml-2">Tým:</span>
                <select
                    value={teamFilter}
                    onChange={(e) => setTeamFilter(e.target.value)}
                    data-testid="matrix-team-filter"
                    className={`bg-black/40 border px-2 py-1 focus:outline-none focus:border-fa-sky ${teamFilter ? "border-fa-sky text-fa-sky font-bold" : "border-white/15 text-white/60"}`}
                >
                    <option value="">Všechny týmy</option>
                    {allTeams.map((t) => (
                        <option key={t} value={t}>{t}</option>
                    ))}
                </select>
                {teamFilter && sortedPlayers.length === 0 && (
                    <button
                        type="button"
                        onClick={() => addWholeTeam(teamFilter)}
                        disabled={addingTeam || rosterLoading}
                        data-testid="matrix-add-whole-team"
                        className="px-2 py-1 border border-fa-mint text-fa-mint hover:bg-fa-mint hover:text-black disabled:opacity-40 flex items-center gap-1"
                        title={`Přidat všechny hráče týmu ${teamFilter} z FPL databáze`}
                    >
                        <Plus size={10} weight="bold" />
                        Přidat celý tým {teamFilter}
                    </button>
                )}
                {teamFilter && sortedPlayers.length > 0 && (() => {
                    // Are there any FPL/Sportmonks players from this team that
                    // aren't in the matrix yet? Show a compact add-rest button.
                    if (!fplRoster) return null;
                    const inMatrix = new Set(matrixPlayers.map((p) => p.fpl_id));
                    const missing = fplRoster.filter((p) =>
                        p.team === teamFilter && !inMatrix.has(p.fpl_id)
                        && !isMarginal(p) && p.status !== "u"
                        && (!posFilter || p.position === posFilter)).length;
                    if (missing === 0) return null;
                    return (
                        <button
                            type="button"
                            onClick={() => addWholeTeam(teamFilter)}
                            disabled={addingTeam || rosterLoading}
                            data-testid="matrix-add-rest-team"
                            className="px-2 py-1 border border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black disabled:opacity-40 flex items-center gap-1"
                            title={`Přidat zbývajících ${missing} hráčů z týmu ${teamFilter}`}
                        >
                            <Plus size={10} weight="bold" />
                            +{missing} zbývá
                        </button>
                    );
                })()}
                <span className="text-white/45 ml-3">Řadit:</span>
                <select
                    value={sortBy.startsWith("phase:") || sortBy.startsWith("gw:") ? "__col__" : sortBy}
                    onChange={(e) => e.target.value !== "__col__" && setSortBy(e.target.value)}
                    data-testid="matrix-sort"
                    className="bg-black/40 border border-white/15 px-2 py-1 focus:outline-none focus:border-fa-sky"
                >
                    {(sortBy.startsWith("phase:") || sortBy.startsWith("gw:")) && (
                        <option value="__col__">{`Sloupec: ${sortBy.replace("phase:", "").replace("gw:", "GW")}`}</option>
                    )}
                    <option value="total_expected">Očekávané body</option>
                    <option value="overall_ppg">Dlouhodobý PPG</option>
                    <option value="current_price">Cena</option>
                    <option value="position">Pozice</option>
                    <option value="display_name">Jméno</option>
                </select>
                {(sortBy.startsWith("phase:") || sortBy.startsWith("gw:")) && (
                    <button
                        type="button"
                        onClick={clearColSort}
                        data-testid="matrix-clear-col-sort"
                        className="font-mono text-[9px] uppercase tracking-widest text-white/50 border border-white/15 px-2 py-1 hover:text-white hover:border-white/40"
                    >
                        Zrušit filter sloupce
                    </button>
                )}
                <span className="ml-auto text-white/45">
                    {metric === "ppg"
                        ? <>Barvy: <span className="text-fa-mint">zelená ≥ 5</span> · <span className="text-orange-200">oranžová 3-4</span> · <span className="text-red-200">červená &lt; 3</span></>
                        : <>Barvy: <span className="text-fa-mint">zelená = nadprůměr</span> · <span className="text-white/60">bílá ≈ průměr</span> · <span className="text-red-200">červená = pod-průměr</span></>
                    }
                </span>
            </div>

            {loading && (
                <div className="border border-white/10 bg-[#0d0d0d] p-6 text-center font-mono text-[11px] text-white/45 italic">
                    Počítám matici pro {matrixPlayers.length} hráčů…
                </div>
            )}

            {data && !loading && (
                <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto" data-testid="matrix-table-wrap">
                    <table className="border-separate border-spacing-0 min-w-full">
                        <thead className="sticky top-0 bg-[#0d0d0d] z-10">
                            <tr className="text-white/60 font-mono text-[9px] uppercase tracking-widest">
                                <th className="text-left px-2 py-2 border-r border-b border-white/20 bg-[#0d0d0d] sticky left-0 z-20 min-w-[190px]">
                                    Hráč
                                </th>
                                {mode === "gw" ? (
                                    gws.map((g) => {
                                        const key = `gw:${g}`;
                                        const active = sortBy === key;
                                        return (
                                            <th
                                                key={g}
                                                onClick={() => toggleColSort(key)}
                                                data-testid={`matrix-col-gw-${g}`}
                                                title={`Řadit dle GW${g}`}
                                                className={`text-center px-1 py-2 border-r border-b border-white/20 min-w-[52px] font-mono tabular-nums text-[9px] cursor-pointer hover:bg-white/[0.04] ${active ? "bg-fa-yellow/15 text-fa-yellow" : ""}`}
                                            >
                                                {g}<span className="ml-0.5 opacity-70">{sortIndicator(key)}</span>
                                            </th>
                                        );
                                    })
                                ) : (
                                    phases.map((ph) => {
                                        const key = `phase:${ph.key}`;
                                        const active = sortBy === key;
                                        return (
                                            <th
                                                key={ph.key}
                                                onClick={() => toggleColSort(key)}
                                                data-testid={`matrix-col-${ph.key}`}
                                                title={`Řadit dle ${ph.label}`}
                                                className={`text-center px-2 py-2 border-r border-b border-white/20 min-w-[85px] font-mono text-[9px] cursor-pointer hover:bg-white/[0.04] ${active ? "bg-fa-yellow/15 text-fa-yellow" : ""}`}
                                            >
                                                {ph.label}<span className="ml-1 opacity-70">{sortIndicator(key)}</span>
                                            </th>
                                        );
                                    })
                                )}
                                <th
                                    onClick={() => toggleColSort("total_expected")}
                                    data-testid="matrix-col-total"
                                    title="Řadit dle součtu 38 GW"
                                    className={`text-right px-2 py-2 border-b border-white/20 min-w-[70px] font-mono text-[9px] cursor-pointer hover:bg-white/[0.04] ${sortBy === "total_expected" ? "bg-fa-yellow/15 text-fa-yellow" : ""}`}
                                >
                                    Σ <span className="opacity-70">{sortIndicator("total_expected")}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedPlayers.map((p) => {
                                const expanded = expandedId === p.fpl_id;
                                const local = matrixPlayers.find((x) => x.fpl_id === p.fpl_id);
                                const isPromoted = !!local?.is_promoted;
                                const priceLabel = (isPromoted || local?.current_price == null || p.current_price == null)
                                    ? "£—"
                                    : `£${p.current_price.toFixed(1)}`;
                                return (
                                    <React.Fragment key={p.fpl_id}>
                                        <tr
                                            className={`border-b border-white/[0.05] hover:bg-white/[0.02] cursor-pointer ${expanded ? "bg-white/[0.03]" : ""}`}
                                            onClick={() => setExpandedId(expanded ? null : p.fpl_id)}
                                            data-testid={`matrix-row-${p.fpl_id}`}
                                        >
                                            <td className="px-2 py-1 border-r border-b border-white/10 bg-[#0d0d0d] sticky left-0 z-10 group">
                                                <div className="flex items-center gap-1">
                                                    <span className="font-mono text-[8px] uppercase tracking-widest text-white/40 w-8">{p.position}</span>
                                                    <span className="font-display font-black text-[12px] truncate max-w-[100px]" title={p.display_name}>
                                                        {p.display_name}
                                                    </span>
                                                    {isPromoted && (
                                                        <span
                                                            className="font-mono text-[7px] uppercase tracking-widest px-[3px] py-[1px] bg-fa-yellow/25 text-fa-yellow border border-fa-yellow/50"
                                                            title="Nově postupující tým — FPL data zatím nejsou"
                                                            data-testid={`matrix-promoted-badge-${p.fpl_id}`}
                                                        >
                                                            Nově
                                                        </span>
                                                    )}
                                                    <span className="ml-auto font-mono text-[9px] tabular-nums text-fa-yellow">
                                                        {priceLabel}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); removePlayer(p.fpl_id); }}
                                                        aria-label={`Odebrat ${p.display_name}`}
                                                        title="Odebrat z matice"
                                                        data-testid={`matrix-remove-${p.fpl_id}`}
                                                        className="ml-1 text-white/25 hover:text-red-300 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <X size={11} weight="bold" />
                                                    </button>
                                                </div>
                                                <div className="font-mono text-[8px] text-white/40 tabular-nums">
                                                    {p.team} · hist {p.overall_ppg.toFixed(1)} PPG
                                                </div>
                                            </td>
                                            {mode === "gw" ? (
                                                p.cells.map((c) => <MatrixCell key={c.gw} c={c} active={expanded} metric={metric} />)
                                            ) : (
                                                phases.map((ph) => (
                                                    <PhaseCell key={ph.key} ph={p.phase_expected[ph.key]} metric={metric} />
                                                ))
                                            )}
                                            <td className="px-2 py-1 text-right border-b border-white/10 tabular-nums font-mono text-[11px] text-fa-mint font-bold">
                                                {p.total_expected.toFixed(0)}
                                            </td>
                                        </tr>
                                        {expanded && <ExpandedRow p={p} mode={mode} colCount={colCount + 1} />}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
